/**
 * Generic hook for updating single-order details in Postgres.
 * @module useUpdateOrderDetails
 */

import { useCallback, useEffect } from "react";

import {
  singleOrderDetailsGenericUpdateForPostgres,
  SingleOrderDetailsGenericUpdateResponse,
} from "../../api/postgreSQL/postCalls/singleOrderDetailsGenericUpdateForPostgres";
import { SingleOrderDetailsTableName } from "../../enums/singleOrderDetailsTableName";
import { useAbortableFetch } from "../useAbortableFetch";

/** Parameters for the {@link useUpdateOrderDetails} hook. */
interface UseUpdateOrderDetailsProps {
  /** PA order ID to update. */
  orderId: string;
  /** Organization ID scoping the query. */
  orgId: string;
  /** Target Postgres table to update. */
  tableName: SingleOrderDetailsTableName;
}

/**
 * Provides an abortable update function for a single order's details in Postgres.
 *
 * Unlike the read hooks, this does not auto-fetch on mount. Instead it exposes
 * a `runUpdate(data)` function that callers invoke with the update payload.
 * In-flight requests are automatically cancelled on unmount.
 *
 * @typeParam T - Shape of the update payload.
 * @param props - Order ID, org ID, and the target table name.
 * @returns runUpdate - Triggers the update with the given data payload.
 * @returns abort    - Manually cancel any in-flight update request.
 * @returns data     - The update response (null until a request completes).
 * @returns loading  - Whether an update request is in progress.
 * @returns error    - Error object if the update failed.
 */
export const useUpdateOrderDetails = <T,>(
  props: UseUpdateOrderDetailsProps,
) => {
  const { orderId, orgId, tableName } = props;

  const fetchFn = useCallback(
    async (signal: AbortSignal, updateData: T, siblingOrderIds?: string[]) => {
      return singleOrderDetailsGenericUpdateForPostgres<T>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: tableName,
          data: updateData,
          sibling_order_ids: siblingOrderIds,
        },
        signal,
      );
    },
    [orderId, orgId, tableName],
  );

  const { runFetch, abort, data, loading, error } =
    useAbortableFetch<SingleOrderDetailsGenericUpdateResponse>(fetchFn);

  useEffect(() => {
    return () => abort();
  }, [abort]);

  const runUpdate = useCallback(
    async (updateData: T, siblingOrderIds?: string[]) =>
      runFetch(updateData, siblingOrderIds),
    [runFetch],
  );

  return { runUpdate, abort, data, loading, error };
};
